import { Link, NavLink } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { COMPANY, NAV_LINKS, ROUTES } from '@/lib/data';
import { cn } from '@/lib/utils';
import { ReservationButton } from '@/components/public/ReservationButton';

export const Header = () => {
    const [open, setOpen] = useState(false);
    return (
        <header data-testid="site-header" className="sticky top-0 z-40 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-b border-border">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-28 flex items-center justify-between gap-3">
                <Link to={ROUTES.home} data-testid="header-logo-link" className="flex items-center shrink-0" aria-label={COMPANY.name}>
                    <img src={COMPANY.logo} alt={COMPANY.name} className="h-20 sm:h-24 w-20 sm:w-24 object-contain" />
                </Link>

                <nav data-testid="header-nav" className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === ROUTES.home}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={({ isActive }) =>
                                cn(
                                    'px-6 py-2 text-sm font-bold font-display rounded-full transition-all',
                                    isActive
                                        ? 'bg-gradient-fun text-white shadow-pop'
                                        : 'text-foreground/80 hover:text-foreground hover:bg-[hsl(var(--kche-yellow))]/30'
                                )
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-2">
                    <ReservationButton
                        from="header"
                        size="sm"
                        tone="gradient"
                        testId="header-reserva-online-button"
                    />
                </div>

                <div className="lg:hidden">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="header-mobile-menu-button"
                                aria-label="Abrir menú"
                                className="inline-flex items-center justify-center size-11 rounded-full bg-gradient-fun text-white shadow-pop hover:brightness-110"
                            >
                                <Menu className="size-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            data-testid="mobile-menu-sheet"
                            className="bg-white w-[88%] sm:w-[420px] p-0 border-l"
                        >
                            <div className="flex flex-col h-full">
                                <div className="p-5 border-b border-border flex items-center justify-between">
                                    <img src={COMPANY.logo} alt={COMPANY.name} className="h-20 w-20 object-contain" />
                                    <button aria-label="Cerrar" onClick={() => setOpen(false)} className="size-9 rounded-full bg-[hsl(var(--kche-pink))]/10 text-[hsl(var(--kche-pink))] inline-flex items-center justify-center"><X className="size-5" /></button>
                                </div>
                                <nav className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
                                    {NAV_LINKS.map((l, i) => {
                                        const colors = ['bg-[hsl(var(--kche-yellow))]/15 text-[hsl(var(--kche-pink))]', 'bg-[hsl(var(--kche-cyan))]/15 text-[hsl(var(--kche-blue))]', 'bg-[hsl(var(--kche-pink))]/12 text-[hsl(var(--kche-pink))]', 'bg-[hsl(var(--kche-green))]/15 text-[hsl(var(--kche-green))]', 'bg-[hsl(var(--kche-purple))]/12 text-[hsl(var(--kche-purple))]'];
                                        return (
                                            <NavLink
                                                key={l.to}
                                                to={l.to}
                                                end={l.to === ROUTES.home}
                                                onClick={() => setOpen(false)}
                                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                                                className={({ isActive }) =>
                                                    cn(
                                                        'px-5 py-4 rounded-2xl text-lg font-bold font-display transition-all',
                                                        isActive ? 'bg-gradient-fun text-white shadow-pop' : colors[i % colors.length] + ' hover:scale-[1.01]'
                                                    )
                                                }
                                            >
                                                {l.label}
                                            </NavLink>
                                        );
                                    })}
                                </nav>
                                <div className="p-4 border-t border-border">
                                    <ReservationButton
                                        from="mobile-menu"
                                        size="md"
                                        tone="gradient"
                                        full
                                        testId="mobile-reserva-online-button"
                                    />
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Header;
